import { generateId } from './contentUtils';

export const deepClone = (value) => {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
};

const ID_KEYS = new Set(['id', 'blockId', 'itemId', 'cardId', 'questionId', 'citationId']);

const collectIdMap = (value, idMap, generateIdFn) => {
  if (Array.isArray(value)) {
    value.forEach((entry) => collectIdMap(entry, idMap, generateIdFn));
    return;
  }
  if (!value || typeof value !== 'object') return;
  Object.entries(value).forEach(([key, entry]) => {
    if (ID_KEYS.has(key) && typeof entry === 'string' && entry) {
      if (!idMap.has(entry)) idMap.set(entry, generateIdFn());
    }
    collectIdMap(entry, idMap, generateIdFn);
  });
};

const replaceIds = (value, idMap, slideId) => {
  if (Array.isArray(value)) return value.map((entry) => replaceIds(entry, idMap, slideId));
  if (!value || typeof value !== 'object') {
    return typeof value === 'string' && idMap.has(value) ? idMap.get(value) : value;
  }
  return Object.fromEntries(Object.entries(value).map(([key, entry]) => {
    if (key === 'slideId') return [key, slideId];
    if (ID_KEYS.has(key) && typeof entry === 'string' && idMap.has(entry)) {
      return [key, idMap.get(entry)];
    }
    return [key, replaceIds(entry, idMap, slideId)];
  }));
};

/** Clone a slide and renew its slide, block, citation, card, item and question IDs. */
export const cloneSlideWithFreshIds = (slide, generateIdFn = generateId) => {
  const cloned = deepClone(slide);
  const newSlideId = generateIdFn();
  const idMap = new Map();
  collectIdMap(cloned, idMap, generateIdFn);
  const renewed = replaceIds(cloned, idMap, newSlideId);
  return { ...renewed, slideId: newSlideId };
};

const VISUAL_KEY = /^(style|styles|className|layout|template|theme|variant|align|alignment|orientation|columns|columnSpan|rowSpan|width|height|minWidth|maxWidth|minHeight|maxHeight|x|y|left|top|right|bottom|position|rotation|scale|opacity|radius|border.*|background.*|fill.*|stroke.*|shadow.*|spacing.*|padding.*|margin.*|gap|color|.*Color|font.*|textAlign|lineHeight|letterSpacing|objectFit|aspectRatio|frame.*|canvas.*)$/i;

const extractVisual = (value) => {
  if (!value || typeof value !== 'object') return undefined;
  if (Array.isArray(value)) {
    const entries = value.map(extractVisual);
    return entries.some(Boolean) ? entries : undefined;
  }
  const result = {};
  Object.entries(value).forEach(([key, entry]) => {
    if (VISUAL_KEY.test(key)) {
      result[key] = deepClone(entry);
      return;
    }
    if (key === 'content' && entry && typeof entry === 'object') {
      const nested = extractVisual(entry);
      if (nested && Object.keys(nested).length) result.content = nested;
    }
  });
  return Object.keys(result).length ? result : undefined;
};

const mergeVisual = (target, format) => {
  if (!format || target == null) return target;
  if (Array.isArray(target) && Array.isArray(format)) {
    return target.map((entry, index) => mergeVisual(entry, format[index]));
  }
  if (typeof target !== 'object' || Array.isArray(target) || Array.isArray(format)) return target;
  const result = { ...target };
  Object.entries(format).forEach(([key, value]) => {
    result[key] = key === 'content' && result.content && typeof result.content === 'object'
      ? mergeVisual(result.content, value)
      : deepClone(value);
  });
  return result;
};

export const captureSlideFormat = ({ slide, background }) => ({
  background: deepClone(background ?? null),
  blocks: (slide?.blocks || []).map((block) => ({
    type: block?.type,
    format: extractVisual(block) || {}
  }))
});

export const applySlideFormat = (slide, captured) => {
  const sourceBlocks = captured?.blocks || [];
  const used = new Set();
  return {
    ...slide,
    blocks: (slide?.blocks || []).map((block, targetIndex) => {
      let sourceIndex = sourceBlocks.findIndex((source, index) => !used.has(index) && source.type === block?.type);
      if (sourceIndex < 0 && sourceBlocks[targetIndex]) sourceIndex = targetIndex;
      if (sourceIndex < 0) return block;
      used.add(sourceIndex);
      return mergeVisual(block, sourceBlocks[sourceIndex].format);
    })
  };
};

